import { createAsyncThunk } from "@reduxjs/toolkit";

import { type TraineeSettings, type WorkoutSettings } from "@fitness-app/data-models/entities/TraineeSettings";

import { type AsyncThunkCreator } from "../../../index";
import { TRAINEE_REDUCER_NAME } from "../types";

type Payload = {
  workoutSettings: TraineeSettings["workoutSettings"];
  id: string;
};

// The pending case writes the new settings into the store before the payload creator runs,
// so the previous values are captured in `condition`, which runs first.
const previousSettings = new WeakMap<Payload, WorkoutSettings | null>();

const changedSettings = (previous: WorkoutSettings | null | undefined, next: WorkoutSettings | null | undefined) => {
  const keys = new Set([...Object.keys(previous ?? {}), ...Object.keys(next ?? {})]) as Set<keyof WorkoutSettings>;
  return [...keys].filter((key) => previous?.[key] !== next?.[key]);
};

export const updateTraineeWorkoutSettings = createAsyncThunk<TraineeSettings, Payload, AsyncThunkCreator<string>>(
  `${TRAINEE_REDUCER_NAME}/updateTraineeWorkoutSettings`,
  async (payload, { rejectWithValue, extra: { db, analytics } }) => {
    const { data, error } = await db
      .from("trainee_settings")
      .update({ workoutSettings: payload.workoutSettings, updatedAt: new Date().toISOString() })
      .eq("id", payload.id)
      .select("*")
      .single<TraineeSettings>();

    if (error || !data) {
      return rejectWithValue(error?.message || "Failed to update notifications settings");
    }

    analytics.track("update_trainee_workout_settings", {
      ...payload.workoutSettings,
      changed: changedSettings(previousSettings.get(payload), payload.workoutSettings),
    });

    return data;
  },
  {
    condition: (payload, { getState }) => {
      previousSettings.set(payload, getState().trainee.traineeSettings?.workoutSettings ?? null);
    },
  },
);
